<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
    modelValue: { type: [String, Number, Boolean], default: '' },
    options: {
        type: Array,
        required: true,
    },
    placeholder: { type: String, default: 'Buscar...' },
    clearable: { type: Boolean, default: true },
    getOptionLabel: {
        type: Function,
        default: (opt) => opt.label
    },
    getOptionValue: {
        type: Function,
        default: (opt) => opt.value !== undefined ? opt.value : opt.id
    },
    actionBtnLabel: { type: String, default: '' },
    // When true, filtering is done externally; options are shown as-is
    remoteSearch: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'change', 'actionBtnClick', 'search']);

const isOpen = ref(false);
const searchQuery = ref('');
const container = ref(null);
const searchInput = ref(null);

// Cache de la etiqueta seleccionada: persiste aunque la lista de opciones cambie
const cachedSelectedLabel = ref('');

const selectedOption = computed(() => {
    return props.options.find(opt => String(props.getOptionValue(opt)) === String(props.modelValue)) || null;
});

const selectedLabel = computed(() => {
    // Primero intenta encontrarlo en las opciones actuales
    if (selectedOption.value) return props.getOptionLabel(selectedOption.value);
    // Si no está en la lista (modo remoto con lista cambiante), usa el cache
    if (props.modelValue !== '' && props.modelValue !== null && cachedSelectedLabel.value) {
        return cachedSelectedLabel.value;
    }
    return '';
});

// Filtra las opciones basándose en el query de búsqueda
const filteredOptions = computed(() => {
    // Si remoteSearch está activado, mostrar las opciones tal como vienen (el padre filtra)
    if (props.remoteSearch) return props.options;

    if (!searchQuery.value) return props.options;

    const query = searchQuery.value.toLowerCase();
    return props.options.filter(opt =>
        String(props.getOptionLabel(opt)).toLowerCase().includes(query)
    );
});

// En modo remoto: indica si el usuario ya ha escrito suficiente para lanzar una búsqueda
const remoteHasSearched = computed(() => props.remoteSearch && searchQuery.value.length >= 3);

// Emitir el evento 'search' cuando cambie el query (útil para búsqueda remota)
watch(searchQuery, (val) => {
    emit('search', val);
});

// Si el modelValue se limpia externamente, limpiar también el cache
watch(() => props.modelValue, (val) => {
    if (val === '' || val === null) cachedSelectedLabel.value = '';
});

const isSelected = (option) => String(props.getOptionValue(option)) === String(props.modelValue);

const openDropdown = () => {
    if (!isOpen.value) {
        isOpen.value = true;
        searchQuery.value = ''; // Limpiamos para que se vean todas al abrir
    }
};

const closeDropdown = () => {
    isOpen.value = false;
    searchQuery.value = ''; // Limpiar la búsqueda en texto al cerrar
    if (searchInput.value) {
        searchInput.value.blur();
    }
};

const selectOption = (option) => {
    const val = props.getOptionValue(option);
    // Guardar la etiqueta en cache antes de que la lista pueda cambiar
    cachedSelectedLabel.value = props.getOptionLabel(option);
    emit('update:modelValue', val);
    emit('change', val);
    closeDropdown();
};

const clearSelection = () => {
    cachedSelectedLabel.value = '';
    emit('update:modelValue', '');
    emit('change', '');
    searchQuery.value = '';
    // Mantenemos abierto si el usuario quiere buscar otra cosa
    if (searchInput.value) {
        searchInput.value.focus();
    }
};

// Cerrar si hacen clic fuera del componente
const handleClickOutside = (event) => {
    if (container.value && !container.value.contains(event.target)) {
        closeDropdown();
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
    <div class="relative w-full" ref="container">
        <!-- Input wrapper que muestra la opción seleccionada o permite buscar -->
        <div class="flex items-center justify-between w-full px-3 py-1.5 min-h-[38px] text-sm bg-white border border-gray-300 hover:border-gray-400 focus:border-gray-500 rounded-md cursor-text font-normal text-gray-700 transition-colors" :class="{ 'ring-4 ring-gray-500/20': isOpen }" @click="openDropdown">
            <input ref="searchInput" v-model="searchQuery" type="text" :placeholder="isOpen ? placeholder : (selectedLabel || placeholder)" class="w-full truncate" style="padding: 0px !important; margin: 0px !important; background: transparent !important; border: none !important; box-shadow: none !important; outline: none !important; font-weight: 400 !important;" :class="(selectedLabel && !isOpen) ? 'placeholder-gray-800' : 'placeholder-gray-500'" @focus="openDropdown" />

            <div class="flex items-center gap-2 pl-2">
                <!-- Botón para limpiar -->
                <i v-if="modelValue !== '' && modelValue !== null && clearable" class="fa-solid fa-xmark text-gray-400 hover:text-red-500 cursor-pointer transition-colors" @click.stop="clearSelection" title="Limpiar selección"></i>

                <!-- Icono de flecha -->
                <i class="fa-solid fa-chevron-down text-gray-500 text-[10px] transition-transform duration-200 cursor-pointer" :class="{ 'rotate-180': isOpen }"></i>
            </div>
        </div>

        <!-- Menú desplegable -->
        <Transition enter-active-class="transition duration-100 ease-out" enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100" leave-active-class="transition duration-75 ease-in" leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
            <div v-show="isOpen" class="absolute z-[100] w-full mt-1 overflow-auto bg-white border border-gray-300 rounded-xl shadow-2xl max-h-72 custom-scrollbar font-normal text-left text-gray-700">
                <ul class="py-1 !m-0 !px-0 !list-none">
                    <!-- Hint: aún no hay query suficiente y no hay opciones para mostrar -->
                    <li v-if="filteredOptions.length === 0 && searchQuery.length < 3 && remoteSearch"
                        class="px-3 py-2 text-sm text-gray-400 cursor-default text-center italic">
                        Escribe al menos 3 caracteres para buscar
                    </li>

                    <!-- Sin resultados reales (búsqueda ejecutada ≥3 chars pero vacía, o modo local sin coincidencias) -->
                    <li v-else-if="filteredOptions.length === 0 && (remoteHasSearched || !remoteSearch)"
                        class="px-3 py-2 text-sm text-gray-500 cursor-default text-center">
                        <div class="mb-1">No se encontraron resultados</div>
                        <div v-if="actionBtnLabel" class="mt-2">
                            <button @click.stop.prevent="emit('actionBtnClick'); closeDropdown()" type="button" class="w-full px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shadow-sm flex items-center justify-center gap-1.5">
                                <i class="fa-solid fa-plus"></i> {{ actionBtnLabel }}
                            </button>
                        </div>
                    </li>
                    <li v-for="option in filteredOptions" :key="getOptionValue(option)" 
                        @click="(!option.disabled && !option.isHeader) ? selectOption(option) : null" 
                        class="px-3 py-1.5 text-sm transition ease-in-out duration-150 flex items-center justify-between"
                        :class="[
                            (option.disabled || option.isHeader) ? 'cursor-default font-bold text-gray-800 bg-gray-100/70 border-b border-gray-100' : 'cursor-pointer hover:bg-[#1967D2] hover:text-white group',
                            (option.is_parent || option.isParent || option.bold) && !option.isHeader ? 'font-bold text-gray-900 bg-gray-50/60' : '',
                            (option.is_child || option.isChild || option.indent || (option.id_parent && option.id_parent > 0)) ? 'pl-7 text-gray-600' : '',
                            isSelected(option) ? 'bg-blue-50 text-blue-700 font-semibold' : ''
                        ]">
                        <div class="flex items-center gap-1.5 truncate">
                            <span v-if="option.is_child || option.isChild || option.indent || (option.id_parent && option.id_parent > 0)" class="text-gray-400 text-xs select-none group-hover:text-white/80">↳</span>
                            <span class="truncate" :class="{ 'font-bold': option.is_parent || option.isParent || option.bold, 'font-normal': !(option.is_parent || option.isParent || option.bold) }">{{ getOptionLabel(option) }}</span>
                        </div>
                        <i v-if="isSelected(option)" class="fa-solid fa-check text-blue-600 group-hover:text-white"></i>
                    </li>
                </ul>
            </div>
        </Transition>
    </div>
</template>

<style scoped>
/* Scrollbar custom mínima solo para este dropdown si es necesario */
.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 20px;
}
</style>
